import Vue from 'vue';
import * as SentryBrowser from '@sentry/browser';
import { Vue as VueIntegration } from '@sentry/integrations';

const SENTRY_LEVELS = ['fatal', 'error', 'warning', 'info', 'debug'];

const MAP_ENV_TO_PROPER_NAME = {
  dev: 'development',
  stage: 'staging',
};

export const getSentryDsn = () => {
  return process.env.VUE_APP_SENTRY_DSN;
};

export const getSentryEnv = () => {
  // VUE_APP_ENV is local, dev, stage or production-sass
  // MAP_ENV_TO_PROPER_NAME is used to translate stage to staging and dev to development
  return MAP_ENV_TO_PROPER_NAME[process.env.VUE_APP_ENV] || process.env.VUE_APP_ENV || 'VUE_APP_ENV IS MISSING';
};

export const getSentryLevel = (level, fallback = 'info') => {
  return SENTRY_LEVELS.indexOf(level) >= 0 ? level : fallback;
};

export const shouldUseSentry = () => {
  const sentryDsn = getSentryDsn();
  const sentryEnv = getSentryEnv();

  if (!sentryDsn || !sentryEnv) {
    return false;
  }

  switch (sentryEnv) {
    case 'production-saas':
      return true;
    case 'staging':
      return true;
    case 'development':
      return true;
    default:
      return false;
  }
};

export const sentrySetup = () => {
  const sentryEnv = getSentryEnv();
  const sentryDsn = getSentryDsn();

  if (shouldUseSentry()) {
    // Initialize Sentry error reporting
    // https://docs.sentry.io/platforms/javascript/vue/
    SentryBrowser.init({
      dsn: sentryDsn,
      integrations: [new VueIntegration({ Vue, attachProps: true, logErrors: true })],
      environment: sentryEnv,
      release: process.env.PACKAGE_VERSION || '---',
    });
  } else {
    // eslint-disable-next-line no-console
    console.log(
      `SENTRY NOT INITIALIZED, ${Boolean(sentryDsn) ? 'SENTRY_DSN AVAILABLE' : 'SENTRY_DSN IS MISSING'}, ${sentryEnv}`
    );
  }
};

// Import and use to manually log messages to Sentry
export const logMessage = (message, level = 'info') => {
  if (!shouldUseSentry()) {
    console.warn(level, message);
    return;
  }

  const sentryLevel = getSentryLevel(level);
  console.warn(level, message);
  SentryBrowser.captureMessage(message, sentryLevel);
};

// Import and use to manually capture exceptions to Sentry
// For example for errors that are caught/handled but we still
// need to know how often they happen.
// Automatically fallback to `logMessage` if `ex` is just a string.
export const logException = (ex, options = {}) => {
  if (!shouldUseSentry()) {
    console.error(ex);
    return;
  }

  options.level = getSentryLevel(options.level, 'error');

  if (typeof ex === 'string') {
    // if ex is not an error but a string, use captureMessage
    logMessage(ex, options.level);
    return;
  }

  const { tags, extra, contexts, level } = options;
  console.error(ex);
  SentryBrowser.captureException(ex, {
    // 'fatal', 'error', 'warning', 'info', or 'debug'.
    // 'error' is the default, 'fatal' is the most severe and 'debug' is the least severe.
    // https://docs.sentry.io/platforms/javascript/#setting-the-level
    level: getSentryLevel(level, 'error'),
    // { [key]: value}
    // tags are searchable key/value pairs attached to events
    // https://docs.sentry.io/platforms/javascript/#tagging-events
    tags: tags || {},
    // { [key]: value}
    // appears in the “Additional Data” section of an event.
    // https://docs.sentry.io/platforms/javascript/#extra-context
    extra: extra || {},
    // { [key]: value}
    // each key set in contexts gets its own section on the issue page,
    // with the section title being the key name.
    // https://docs.sentry.io/platforms/javascript/#extra-context
    contexts: contexts || {},
  });
};
